import React from 'react';
import { inject, observer } from 'mobx-react';
import styles from './index.module.scss';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import { BASE_IMG_URL, livePhotoImage, photographyImage } from '@images/imageUrls';
import classNames from 'classnames';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { BaseComponent } from '@components/Base/h5/BaseComponent';
import type { BaseRouteComponentProps } from '../../../types/base';
import { formatDateTime } from '@utils/timeUtils';
import { formatNumberToWan } from '@utils/formatNumber';
import InfiniteScrollList from '@components/InfiniteScrollList';
import { highlightname } from '@utils/high-light-name';
import Avatar from '@components/avatar';
import Toast from '@components/Toast';
import updateImageListSrc from '@utils/update-image-list-src';
import { px2rem } from '@utils/px2rem';

const LIVE_ICON_URL = livePhotoImage.liveIcon;

// 定义组件自身的 props
interface OwnProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    data: any;   // 必需的数据
    type?: any;  // 类型 1：正常作品列表 2：我的作品
    loading?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
    emptyText?: string; // 为空时提示文字
    emptyIcon?: string; // 为空时icon
    emptyBoxStyle?: any; // 为空状态时的自定义样式
    searchKeyword?: any; // 搜索关键词，用于高亮显示
    voteClick?: Function;   // 点击投票
    isShowTopThree?: boolean    // 是否展示前三名特殊样式
    showVideoIcon?: boolean; // 是否展示视频标识，默认展示
    showWorkRanking?: boolean; // 展示作品实际排名，而非列表序号
    photographyDetail?: any // 摄影大赛详情
    canvassForVotesClick?: Function;   // 点击为TA拉票
    currentItemClick?: Function;   // 点击打开详情
}

// 合并路由 props
type H5PhotographyListProps = OwnProps & RouteComponentProps;

interface H5PhotographyListState {
    loading?: boolean;
    error?: any;
    pageLoaded?: any;
    currentImgDetail: any;  // 当前点击的图片数据
    // currentImgDetailShowStatus: boolean // 当前点击的图片信息弹框是否显示
    
}

@inject('app', 'site', 'user')
@observer
class H5PhotographyList extends React.Component<H5PhotographyListProps, H5PhotographyListState> {
    constructor(props: H5PhotographyListProps) {
        super(props);
        this.state = {
            currentImgDetail: {},   // 当前点击的图片详情数据
            // currentImgDetailShowStatus: false,  // 当前点击的图片信息弹框是否显示
        };
    }

    componentDidMount() {
        // 可以在这里添加初始化逻辑
        console.log('这里是H5');
    }

    // 渲染单项 - 根据提供的数据结构
    renderPhotographyItem = (item: any, index: number) => {
        let { data, searchKeyword, type, voteClick, photographyDetail, currentItemClick, isShowTopThree, showVideoIcon = true, showWorkRanking = false, user } = this.props;
        const isLiveWork = String(item?.type || '').toLowerCase() === 'live';
        const isVideoWork = String(item?.type || '').toLowerCase() === 'video';
        const ranking = Number(item?.ranking);
        const rankText = ranking > 100 ? '100+' : ranking > 0 ? ranking : '--';
        
        return (
            <div key={item.id || index} className={styles.photographyItem}>
                <div className={styles.photographyCard}>
                    <div onClick={() => {
                        this.setState({
                            currentImgDetail: item,
                        })
                        currentItemClick && currentItemClick(item)
                    }} className={styles.cardLeft}>
                        <div className={styles.photographyIndex}>{showWorkRanking ? rankText : (isShowTopThree && data.length > 13) ? index + 4 : index + 1}</div>
                        {item.image && (
                            <div className={styles.photographyImgBox}>
                                {isLiveWork && <img src={LIVE_ICON_URL} alt="" className={styles.liveTagIcon} />}
                                {showVideoIcon && isVideoWork && <img src={photographyImage.videoIcon} alt="视频作品" className={styles.liveTagIcon} draggable={false} />}
                                <img 
                                    src={updateImageListSrc(item.image, 4)} 
                                    alt={item.name}
                                    className={styles.photographyImg}
                                    onError={(e) => {
                                        e.currentTarget.src = `${BASE_IMG_URL}/common/null.png`;
                                    }}
                                />
                            </div>
                        )}
                        
                        <div className={styles.photographyInfo}>  
                            <div className={styles.infoTopBox}>
                                <div className={styles.photographyNumber}>ID:{item.number}</div>
                                <div className={styles.photographyName}>{item.name}</div>
                            </div>
                            <div className={styles.progressOuterBox}>
                                <div className={styles.progressBox}>
                                    <div className={styles.progress} style={{width: item.progress + '%'}}></div>
                                </div>
                                <div className={styles.photographyVoteCount}>{item.voteCount}票</div>
                            </div>
                        </div>
                    </div>
                    {(photographyDetail.status == 1 && item.user?.code == user?.userInfo?.code) && <div className={classNames(styles.voteBtn)} onClick={() => {
                        this.setState({
                            currentImgDetail: item
                        }, () => {
                            this.canvassForVotes()
                        })
                    }}>拉票</div>}
                    {(photographyDetail.status == 1 && item.user?.code != user?.userInfo?.code) && <div className={classNames(styles.voteBtn, item.isVoted ? styles.votedBtn : '')} onClick={() => voteClick && voteClick(item)}>{item.isVoted ? '已投' : '投票'}</div>}
                </div>
            </div>
        );
    }

    // 点击拉票
    canvassForVotes = () => {
        const { photographyDetail, canvassForVotesClick } = this.props;
        let { currentImgDetail } = this.state;
        if(photographyDetail.status == 2) { // 如果已结束
            Toast.info('活动已结束')
        } else {
            canvassForVotesClick && canvassForVotesClick(currentImgDetail);
        }
    }

    renderContent() {
        const { 
            data = [],  // 提供默认空数组，避免 undefined
            loading = false, 
            hasMore = true, 
            emptyText, 
            emptyIcon, 
            emptyBoxStyle, 
            onLoadMore,
            isShowTopThree = false,
            showVideoIcon = true,
            voteClick,
            photographyDetail,
            currentItemClick,
            user
        } = this.props;
        let { currentImgDetail } = this.state;
        
        if (!Array.isArray(data)) return null;
        
        // 如果是空数据，显示空状态
        if (data.length === 0) {
            return (
                <InfiniteScrollList
                    variant="h5"
                    data={[]}
                    loading={loading}
                    hasMore={hasMore}
                    onLoadMore={onLoadMore || (() => {})}
                    renderItem={() => null}
                    emptyText={''}
                    emptyIcon={''}
                    emptyBoxStyle={''}
                />
            );
        }
        let dataList = data?.length >= 13 ? data.slice(3) : data;    // 如果数据超过13条，就显示前三名单独样式
        console.log(data, '人气列表', isShowTopThree);
        return (
            <div className={styles.photographyContainer}>
                {(isShowTopThree && data.length > 13) && <>
                    <div className={styles.topThreeBox}>
                        <div className={styles.sidesItem}>
                            <div className={styles.sidesImgBox} onClick={() => {
                                this.setState({
                                    currentImgDetail: data[1],
                                })
                                currentItemClick && currentItemClick(data[1])
                            }}>
                                {String(data[1]?.type || '').toLowerCase() === 'live' && <img src={LIVE_ICON_URL} alt="" className={classNames(styles.liveTagIcon, styles.topThreeLiveTagIcon)} />}
                                {showVideoIcon && String(data[1]?.type || '').toLowerCase() === 'video' && <img src={photographyImage.videoIcon} alt="视频作品" className={classNames(styles.liveTagIcon, styles.topThreeLiveTagIcon)} draggable={false} />}
                                <img 
                                    src={updateImageListSrc(data[1]?.image, 4)} 
                                    alt={data[1]?.name}
                                    className={styles.sidesImg}
                                    onError={(e) => {
                                        e.currentTarget.src = `${BASE_IMG_URL}/common/null.png`;
                                    }}
                                />
                                <img src={`${BASE_IMG_URL}/photography/two.png`} alt="" className={styles.rankIcon} />
                            </div>
                            <div className={styles.photographyNumber}>ID:{data[1]?.number}</div>
                            <div className={styles.photographyName}>{data[1]?.name}</div>
                            <div className={styles.photographyVoteCount}>{data[1]?.voteCount}票</div>
                            {(photographyDetail.status == 1 && data[1]?.user?.code == user?.userInfo?.code) && <div className={classNames(styles.voteBtn)} onClick={() => {
                                this.setState({
                                    currentImgDetail: data[1]
                                }, () => {
                                    this.canvassForVotes()
                                })
                            }}>拉票</div>}
                            {(photographyDetail.status == 1 && data[1]?.user?.code != user?.userInfo?.code) && <div className={classNames(styles.voteBtn, data[1]?.isVoted ? styles.votedBtn : '')} onClick={() => voteClick && voteClick(data[1])}>{data[1]?.isVoted ? '已投' : '投票'}</div>}       
                        </div>
                        <div className={classNames(styles.sidesItem, styles.centerItem)}>
                            <div className={styles.sidesImgBox} onClick={() => {
                                this.setState({
                                    currentImgDetail: data[0],
                                })
                                currentItemClick && currentItemClick(data[0])
                            }}>
                                {String(data[0]?.type || '').toLowerCase() === 'live' && <img src={LIVE_ICON_URL} alt="" className={classNames(styles.liveTagIcon, styles.topThreeLiveTagIcon)} />}
                                {showVideoIcon && String(data[0]?.type || '').toLowerCase() === 'video' && <img src={photographyImage.videoIcon} alt="视频作品" className={classNames(styles.liveTagIcon, styles.topThreeLiveTagIcon)} draggable={false} />}
                                <img 
                                    src={updateImageListSrc(data[0]?.image, 4)} 
                                    alt={data[0]?.name}
                                    className={styles.sidesImg}
                                    onError={(e) => {
                                        e.currentTarget.src = `${BASE_IMG_URL}/common/null.png`;
                                    }}
                                />
                                <img src={`${BASE_IMG_URL}/photography/one.png`} alt="" className={styles.rankIcon} />
                            </div>
                            <div className={styles.photographyNumber}>ID:{data[0]?.number}</div>
                            <div className={styles.photographyName}>{data[0]?.name}</div>
                            <div className={styles.photographyVoteCount}>{data[0]?.voteCount}票</div>
                            {(photographyDetail.status == 1 && data[0]?.user?.code == user?.userInfo?.code) && <div className={classNames(styles.voteBtn)} onClick={() => {
                                this.setState({
                                    currentImgDetail: data[0]
                                }, () => {
                                    this.canvassForVotes()
                                })
                            }}>拉票</div>}
                            {(photographyDetail.status == 1 && data[0]?.user?.code != user?.userInfo?.code) && <div className={classNames(styles.voteBtn, data[0]?.isVoted ? styles.votedBtn : '')} onClick={() => voteClick && voteClick(data[0])}>{data[0]?.isVoted ? '已投' : '投票'}</div>}
                        </div>
                        <div className={styles.sidesItem}>
                            <div className={styles.sidesImgBox} onClick={() => {
                                this.setState({
                                    currentImgDetail: data[2],
                                })
                                currentItemClick && currentItemClick(data[2])
                            }}>
                                {String(data[2]?.type || '').toLowerCase() === 'live' && <img src={LIVE_ICON_URL} alt="" className={classNames(styles.liveTagIcon, styles.topThreeLiveTagIcon)} />}
                                {showVideoIcon && String(data[2]?.type || '').toLowerCase() === 'video' && <img src={photographyImage.videoIcon} alt="视频作品" className={classNames(styles.liveTagIcon, styles.topThreeLiveTagIcon)} draggable={false} />}
                                <img 
                                    src={updateImageListSrc(data[2]?.image, 4)} 
                                    alt={data[2]?.name}
                                    className={styles.sidesImg}
                                    onError={(e) => {
                                        e.currentTarget.src = `${BASE_IMG_URL}/common/null.png`;
                                    }}
                                />
                                <img src={`${BASE_IMG_URL}/photography/three.png`} alt="" className={styles.rankIcon} />
                            </div>
                            <div className={styles.photographyNumber}>ID:{data[2]?.number}</div>
                            <div className={styles.photographyName}>{data[2]?.name}</div>
                            <div className={styles.photographyVoteCount}>{data[2]?.voteCount}票</div>
                            {(photographyDetail.status == 1 && data[2]?.user?.code == user?.userInfo?.code) && <div className={classNames(styles.voteBtn)} onClick={() => {
                                this.setState({
                                    currentImgDetail: data[2]
                                }, () => {
                                    this.canvassForVotes()
                                })
                            }}>拉票</div>}
                            {(photographyDetail.status == 1 && data[2]?.user?.code != user?.userInfo?.code) && <div className={classNames(styles.voteBtn, data[2]?.isVoted ? styles.votedBtn : '')} onClick={() => voteClick && voteClick(data[2])}>{data[2]?.isVoted ? '已投' : '投票'}</div>}      
                        </div>
                    </div>
                </>}
                <div style={{borderRadius: px2rem(24), overflow: 'hidden'}}>
                    {dataList.map((item, index) => this.renderPhotographyItem(item, index))}
                </div>
                

                {/* 点击某个item，打开详情 */}
                {/* {currentImgDetailShowStatus && <div className={styles.photographyCardDetail}>
                    <div className={styles.detailTiTleBox}>
                        <img src={`${BASE_IMG_URL}/photography/back.png`} alt="" className={styles.detailTiTleBack}  onClick={() => {
                            this.setState({
                                currentImgDetail: {},
                                currentImgDetailShowStatus: false
                            })
                        }}/>
                        <div className={styles.detailTiTle}>{currentImgDetail.name}</div>
                    </div>
                    <div className={styles.detailImgBox}>
                        <img src={currentImgDetail.image} alt="" className={styles.detailImg}/>
                        {currentImgDetail.aigcStatus == 1 && <div className={styles.aiTip}>图片属于AI生成</div>}
                        {currentImgDetail.aigcStatus == 2 && <div className={styles.aiTip}>作者声明：图片由AI生成</div>}
                        {currentImgDetail.aigcStatus == 3 && <div className={styles.aiTip}>图片疑似使用AI生成技术，请谨慎甄别</div>}
                    </div>
                    <div className={styles.detailInfoBox}>
                        <div className={styles.userInfo}>
                            {currentImgDetail.user?.avatar && (<>
                                <Avatar
                                    avatarUrl={currentImgDetail.user?.avatar}
                                    userCode={currentImgDetail.user?.code}
                                    size={60}
                                    titleMark={currentImgDetail.user?.titleMark}
                                    officialSize={24}
                                ></Avatar>
                            </>)}
                            <div className={styles.userNameBox}>
                                <span className={styles.nickname}>{currentImgDetail.user?.nickname}</span>
                                <span className={styles.titleName}>{currentImgDetail.user?.titleName}</span>
                            </div>
                        </div> 
                        <div className={styles.numBox}>
                            <div className={styles.photographyRanking}>NO.{currentImgDetail.ranking}</div>
                            <div className={styles.photographyNumber}>{currentImgDetail.number}</div>
                            <div className={styles.photographyVoteCount}>{currentImgDetail.voteCount}票</div>
                        </div>     
                    </div>
                    <div className={styles.detailBtnBox}>
                        <div className={classNames(styles.canvass, photographyDetail.status == 2 ? styles.canvassGray : '')} onClick={() => this.canvassForVotes()}>为TA拉票</div>
                        <div className={classNames(styles.openAppVote, photographyDetail.status == 2 ? styles.openAppVoteGray : '')}>打开App端投票</div>
                    </div>
                </div>} */}

                {/* 加载更多 */}
                {hasMore && (
                    <div className={styles.loadMoreContainer}>
                        {loading ? (
                            <div className={styles.loadingText}>加载中...</div>
                        ) : (
                            <button 
                                className={styles.loadingText}
                                onClick={onLoadMore}
                            >
                                加载更多
                            </button>
                        )}
                    </div>
                )}
                
                {!hasMore && data.length > 0 && (
                    <div className={styles.noMoreText}>没有更多数据了</div>
                )}
            </div>
        );
    }

    render() {
        return this.renderContent();
    }
}

export default withRouter(H5PhotographyList);
